import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  Tooltip,
  Grid,
  Card,
  CardContent,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
} from "@material-ui/icons";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";
import ConfirmationModal from "../ConfirmationModal";
import TableRowSkeleton from "../TableRowSkeleton";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  statsContainer: {
    marginBottom: theme.spacing(3),
  },
  statsCard: {
    textAlign: "center",
    padding: theme.spacing(2),
    background: `linear-gradient(135deg, ${theme.palette.secondary.main}20, ${theme.palette.secondary.main}10)`,
    border: `1px solid ${theme.palette.secondary.main}30`,
  },
  statsNumber: {
    fontSize: "1.5rem",
    fontWeight: "bold",
    color: theme.palette.secondary.main,
  },
  statsLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  table: {
    minWidth: 650,
  },
  categoryName: {
    fontWeight: 500,
    marginBottom: 4,
  },
  categoryDescription: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  productCount: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  actionButton: {
    padding: 4,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  dialogContent: {
    padding: theme.spacing(3),
    minWidth: 400,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
}));

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Nome é obrigatório")
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(50, "Nome deve ter no máximo 50 caracteres"),
  description: Yup.string()
    .max(200, "Descrição deve ter no máximo 200 caracteres"),
});

const CategoryManager = ({ onRefresh, refreshTrigger }) => {
  const classes = useStyles();
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [stats, setStats] = useState({
    totalCategories: 0,
    activeCategories: 0,
    totalProducts: 0,
  });

  useEffect(() => {
    loadCategories();
    loadStats();
  }, [refreshTrigger]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/catalog/categories");
      setCategories(data.categories);
    } catch (err) {
      console.error("Error loading categories:", err);
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get("/catalog/stats");
      setStats({
        totalCategories: data.totalCategories,
        activeCategories: data.totalCategories, // Assuming all categories are active
        totalProducts: data.totalProducts,
      });
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  };

  const handleAddCategory = () => {
    setSelectedCategory(null);
    setFormModalOpen(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setFormModalOpen(true);
  };

  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setDeleteModalOpen(true);
  };

  const handleFormSubmit = async (values, { setSubmitting }) => {
    try {
      if (selectedCategory) {
        await api.put(`/catalog/categories/${selectedCategory.id}`, values);
        toastUtils.success("Categoria atualizada com sucesso!");
      } else {
        await api.post("/catalog/categories", values);
        toastUtils.success("Categoria criada com sucesso!");
      }
      
      setFormModalOpen(false);
      setSelectedCategory(null);
      loadCategories();
      loadStats();
      onRefresh();
    } catch (err) {
      console.error("Error saving category:", err);
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/catalog/categories/${selectedCategory.id}`);
      toastUtils.success("Categoria excluída com sucesso!");
      setDeleteModalOpen(false);
      setSelectedCategory(null);
      loadCategories();
      loadStats();
      onRefresh();
    } catch (err) {
      console.error("Error deleting category:", err);
      toastError(err);
    }
  };

  const renderStatsCards = () => (
    <Grid container spacing={3} className={classes.statsContainer}>
      <Grid item xs={12} sm={4}>
        <Card className={classes.statsCard}>
          <CardContent>
            <Typography className={classes.statsNumber}>
              {stats.totalCategories}
            </Typography>
            <Typography className={classes.statsLabel}>
              Total de Categorias
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Card className={classes.statsCard}>
          <CardContent>
            <Typography className={classes.statsNumber}>
              {stats.activeCategories}
            </Typography>
            <Typography className={classes.statsLabel}>
              Categorias Ativas
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Card className={classes.statsCard}>
          <CardContent>
            <Typography className={classes.statsNumber}>
              {stats.totalProducts}
            </Typography>
            <Typography className={classes.statsLabel}>
              Produtos Cadastrados
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <div className={classes.root}>
      {renderStatsCards()}
      
      <Box className={classes.header}>
        <Typography variant="h6">
          Gerenciar Categorias
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddCategory}
          startIcon={<AddIcon />}
        >
          Nova Categoria
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Categoria</TableCell>
                <TableCell>Produtos</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from(new Array(5)).map((_, index) => (
                  <TableRowSkeleton key={index} columns={4} />
                ))
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <div className={classes.emptyState}>
                      <CategoryIcon style={{ fontSize: 48, color: "#ccc", marginBottom: 16 }} />
                      <Typography variant="h6">
                        Nenhuma categoria encontrada
                      </Typography>
                      <Typography variant="body2">
                        Crie categorias para organizar seus produtos
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddCategory}
                        startIcon={<AddIcon />}
                        style={{ marginTop: 16 }}
                      >
                        Criar Primeira Categoria
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category) => (
                  <TableRow key={category.id} hover>
                    <TableCell>
                      <Box>
                        <Typography className={classes.categoryName}>
                          {category.name}
                        </Typography>
                        {category.description && (
                          <Typography className={classes.categoryDescription}>
                            {category.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography className={classes.productCount}>
                        {category.productCount || 0} produtos
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={category.isActive ? 'Ativa' : 'Inativa'}
                        color={category.isActive ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar categoria">
                        <IconButton
                          className={classes.actionButton}
                          onClick={() => handleEditCategory(category)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Excluir categoria">
                        <IconButton
                          className={classes.actionButton}
                          onClick={() => handleDeleteClick(category)}
                          disabled={category.productCount > 0}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Category Form Modal */}
      <Dialog
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedCategory ? "Editar Categoria" : "Nova Categoria"}
        </DialogTitle>
        
        <Formik
          initialValues={{
            name: selectedCategory?.name || "",
            description: selectedCategory?.description || "",
            isActive: selectedCategory?.isActive !== undefined ? selectedCategory.isActive : true,
          }}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <DialogContent className={classes.dialogContent}>
                <TextField
                  fullWidth
                  name="name"
                  label="Nome da Categoria *"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  className={classes.formField}
                />
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  name="description"
                  label="Descrição"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.description && Boolean(errors.description)}
                  helperText={touched.description && errors.description}
                  className={classes.formField}
                />
              </DialogContent>
              
              <DialogActions>
                <Button onClick={() => setFormModalOpen(false)} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Salvando..." : (selectedCategory ? "Atualizar" : "Criar")}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        title="Excluir Categoria"
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      >
        Tem certeza que deseja excluir a categoria "{selectedCategory?.name}"?
        {selectedCategory?.productCount > 0 && (
          <Typography color="error" style={{ marginTop: 8 }}>
            Esta categoria possui {selectedCategory.productCount} produtos associados.
            Mova os produtos para outra categoria antes de excluir.
          </Typography>
        )}
      </ConfirmationModal>
    </div>
  );
};

export default CategoryManager;